import React, { useEffect, useState } from 'react'
import { HT_GOLD_THEMES } from '../lib/htGoldTheme'
import type { ThemeMetadata } from '../config/themes'

const STORAGE_KEY = 'htg-theme-switcher-open'

interface ThemeSwitcherProps {
  themeId: string
  themes: ThemeMetadata[]
  onSwitch: (themeId: string) => void
}

export const ThemeSwitcher: React.FC<ThemeSwitcherProps> = ({ themeId, themes, onSwitch }) => {
  const [open, setOpen] = useState(false)

  useEffect(() => {
    try {
      setOpen(localStorage.getItem(STORAGE_KEY) === '1')
    } catch {
      // ignore
    }
  }, [])

  const toggle = () => {
    setOpen((prev) => {
      const next = !prev
      try {
        localStorage.setItem(STORAGE_KEY, next ? '1' : '0')
      } catch {
        // ignore
      }
      return next
    })
  }

  const active = themes.find((t) => t.id === themeId) || themes[0]
  const darkThemes = themes.filter((t) => HT_GOLD_THEMES[t.id]?.mode !== 'light')
  const whiteThemes = themes.filter((t) => HT_GOLD_THEMES[t.id]?.mode === 'light')

  const renderSwatch = (theme: ThemeMetadata) => {
    const tokens = HT_GOLD_THEMES[theme.id]
    if (!tokens) return null
    return (
      <button
        key={theme.id}
        type="button"
        className={`htg-swatch${theme.id === themeId ? ' is-active' : ''}${tokens.mode === 'light' ? ' htg-swatch--light' : ''}`}
        style={
          {
            '--sw-primary': tokens.primary,
            '--sw-secondary': tokens.secondary,
            '--sw-dark': tokens.dark,
          } as React.CSSProperties
        }
        onClick={() => onSwitch(theme.id)}
      >
        <span className="htg-swatch__name">{theme.displayName}</span>
        <span className="htg-swatch__chips" aria-hidden>
          <i style={{ background: tokens.primary }} />
          <i
            style={{
              background:
                tokens.mode === 'light'
                  ? `color-mix(in srgb, ${tokens.secondary} 70%, ${tokens.primary} 30%)`
                  : tokens.secondary,
            }}
          />
          <i
            style={{
              background:
                tokens.mode === 'light'
                  ? `color-mix(in srgb, ${tokens.secondary} 40%, #142033)`
                  : tokens.dark,
              borderColor: tokens.mode === 'light' ? '#64748b' : undefined,
            }}
          />
        </span>
      </button>
    )
  }

  if (!open) {
    return (
      <button
        type="button"
        className="htg-theme-fab"
        onClick={toggle}
        aria-label="แสดงตัวเลือกธีม"
        title={`ธีม: ${active?.displayName || themeId}`}
      >
        <span className="htg-theme-fab__icon" aria-hidden />
        <span className="htg-theme-fab__label">ธีม</span>
        <span className="htg-theme-fab__name">{active?.displayName || themeId}</span>
      </button>
    )
  }

  return (
    <aside className="htg-theme-drawer" aria-label="เลือกธีมสี">
      <div className="htg-theme-drawer__head">
        <div className="htg-theme-drawer__title">
          HT Gold — <span>{active?.displayName}</span>
        </div>
        <button
          type="button"
          className="htg-theme-drawer__hide"
          onClick={toggle}
          aria-label="ซ่อนตัวเลือกธีม"
          title="ซ่อน"
        >
          ›
        </button>
      </div>
      <p className="htg-theme-drawer__hint">20 โทน · Dark / White · ซ่อนได้ด้านขวา</p>
      <div className="htg-theme-bar__group-label">Dark</div>
      <div className="htg-swatches htg-swatches--drawer">{darkThemes.map(renderSwatch)}</div>
      <div className="htg-theme-bar__group-label">White</div>
      <div className="htg-swatches htg-swatches--drawer">{whiteThemes.map(renderSwatch)}</div>
    </aside>
  )
}

export default ThemeSwitcher
